import {
  ArrowCounterClockwise,
  CalendarPlus,
  Heart,
  PencilSimple,
  Signpost,
  Trash,
} from '@phosphor-icons/react';
import { addDays, LEISURE_TEXT, weekdayOf, type Idea, type IdeaPlace } from '@shh/shared';
import { Avatar, Button, ChoiceGroup, Heading, IconButton, Sheet, Surface, Text } from '@shh/ui';
import { useState } from 'react';
import { EventForm } from '../../components/EventForm';
import { IdeaForm } from '../../components/leisureForms';
import { SpotInfo } from '../../components/Spot';
import { deleteIdea, markIdeaPlanned, toggleHeart } from '../../data/api';
import { dayLabel } from '../../data/agenda';
import { memberById } from '../../data/derive';
import { useFamily, type FamilyData, type WithId } from '../../data/family';
import { Empty, SectionHead, SuggestionCard, useLeisureHint, useLeisureRights } from './common';
import { locale } from '../../lib/locale';
import { funText } from '../../i18n/fun';

/** Ideas still waiting – most hearts first. Planned ones come back when the day has passed. */
export const openIdeas = (f: FamilyData) =>
  f.ideas
    .filter((i) => !i.planned)
    .sort((a, b) => b.hearts.length - a.hearts.length || a.title.localeCompare(b.title, 'de'));

type Filter = 'all' | 'out' | 'rain';
const fits = (filter: Filter, place: IdeaPlace) =>
  filter === 'all' || (filter === 'out' ? place !== 'in' : place !== 'out');

/** The next Saturday – where most trips end up. */
const nextSaturday = (today: string) => addDays(today, (6 - weekdayOf(today) + 7) % 7 || 7);

export function IdeasPage() {
  const f = useFamily();
  const rights = useLeisureRights();
  const hint = useLeisureHint();
  const [filter, setFilter] = useState<Filter>('all');
  const [editing, setEditing] = useState<WithId<Idea> | 'new' | null>(null);
  const [planning, setPlanning] = useState<WithId<Idea> | null>(null);
  const open = openIdeas(f).filter((i) => fits(filter, i.place));
  const planned = f.ideas
    .filter((i) => i.planned && i.planned >= f.today)
    .sort((a, b) => a.planned!.localeCompare(b.planned!));
  const done = f.ideas
    .filter((i) => i.planned && i.planned < f.today)
    .sort((a, b) => b.planned!.localeCompare(a.planned!));
  const editable = editing && editing !== 'new' ? editing : undefined;
  const canDelete =
    editable && (rights.adult || (f.meId !== undefined && editable.createdBy === f.meId));

  return (
    <>
      {hint?.kind === 'idea' && <SuggestionCard hint={hint} />}
      <section aria-labelledby="open" className="flex flex-col gap-gap-md">
        <SectionHead
          id="open"
          title={funText.wantToDo}
          onAdd={() => setEditing('new')}
          addLabel={funText.idea}
        />
        <ChoiceGroup
          label={funText.show}
          value={filter}
          onChange={setFilter}
          choices={[
            { value: 'all', label: funText.all },
            { value: 'out', label: funText.outdoors },
            { value: 'rain', label: funText.rainy },
          ]}
        />
        {open.length ? (
          <ul className="grid grid-cols-1 gap-gap-md md:grid-cols-2 xl:grid-cols-3">
            {open.map((i) => (
              <li key={i.id} className="flex">
                <IdeaCard
                  idea={i}
                  onPlan={rights.canPlan ? () => setPlanning(i) : undefined}
                  onEdit={
                    rights.canPlan || i.createdBy === f.meId ? () => setEditing(i) : undefined
                  }
                />
              </li>
            ))}
          </ul>
        ) : (
          <Empty icon={Signpost}>{f.ideas.length ? funText.noMatch : funText.noIdeas}</Empty>
        )}
      </section>

      {planned.length > 0 && (
        <section aria-labelledby="planned" className="flex flex-col gap-gap-md">
          <Heading level={2} size="md" id="planned">
            {funText.planned}
          </Heading>
          <ul className="flex flex-col gap-gap-sm">
            {planned.map((i) => (
              <li key={i.id}>
                <Surface padding="sm" className="flex flex-wrap items-center gap-gap-md">
                  <span className="flex-1 font-bold">{i.title}</span>
                  <span className="text-sm font-semibold text-text-secondary">
                    {dayLabel(f, i.planned!, true).label}, {dayLabel(f, i.planned!).date}
                  </span>
                </Surface>
              </li>
            ))}
          </ul>
        </section>
      )}

      {done.length > 0 && (
        <section aria-labelledby="done" className="flex flex-col gap-gap-md">
          <Heading level={2} size="md" id="done">
            {funText.done}
          </Heading>
          <ul className="flex flex-col gap-gap-sm">
            {done.slice(0, 12).map((i) => (
              <li key={i.id}>
                <Surface
                  tone="sunken"
                  padding="sm"
                  className="flex flex-wrap items-center gap-gap-md"
                >
                  <span className="flex flex-1 flex-col">
                    <span className="font-bold">{i.title}</span>
                    <span className="text-sm text-text-secondary">
                      {funText.onDay(dayLabel(f, i.planned!).date)}
                    </span>
                  </span>
                  {rights.canPlan && (
                    <Button
                      variant="quiet"
                      icon={ArrowCounterClockwise}
                      onPress={() => void markIdeaPlanned(f.householdId, i.id, null)}
                    >
                      {funText.backOnList}
                    </Button>
                  )}
                </Surface>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Sheet
        isOpen={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editable ? funText.editIdea : funText.newIdea}
        isDismissable={false}
      >
        {editing && (
          <div className="flex flex-col gap-gap-lg">
            <IdeaForm idea={editable} onDone={() => setEditing(null)} />
            {canDelete && (
              <Button
                variant="quiet"
                icon={Trash}
                className="self-start"
                onPress={() => {
                  void deleteIdea(f.householdId, editable.id);
                  setEditing(null);
                }}
              >
                {funText.deleteIdea}
              </Button>
            )}
          </div>
        )}
      </Sheet>
      <Sheet
        isOpen={planning !== null}
        onOpenChange={(o) => !o && setPlanning(null)}
        title={funText.plan}
        isDismissable={false}
      >
        {planning && (
          <EventForm
            draft={{
              title: planning.title,
              date: nextSaturday(f.today),
              assignees: 'all',
              start: '10:00',
              notes: planning.note,
              location: planning.spot
                ? [planning.spot.name, planning.spot.address]
                    .filter(Boolean)
                    .join(', ')
                    .slice(0, 120)
                : undefined,
            }}
            onSaved={(day) => markIdeaPlanned(f.householdId, planning.id, day)}
            onDone={() => setPlanning(null)}
          />
        )}
      </Sheet>
    </>
  );
}

function IdeaCard({
  idea,
  onPlan,
  onEdit,
}: {
  idea: WithId<Idea>;
  onPlan?: () => void;
  onEdit?: () => void;
}) {
  const f = useFamily();
  const rights = useLeisureRights();
  const mine = rights.me ? idea.hearts.includes(rights.me) : false;
  const fans = idea.hearts.map((id) => memberById(f, id)).filter((m) => m !== undefined);
  const author = memberById(f, idea.createdBy);
  const meta = [
    LEISURE_TEXT[locale].place[idea.place],
    idea.length && LEISURE_TEXT[locale].length[idea.length],
    idea.cost,
  ].filter(Boolean);

  return (
    <Surface as="article" className="flex w-full flex-col gap-gap-sm">
      <div className="flex items-start gap-gap-sm">
        <div className="flex min-w-0 flex-1 flex-col">
          <Heading level={3} size="sm">
            {idea.title}
          </Heading>
          <Text size="sm" tone="secondary">
            {meta.join(' · ')}
          </Text>
        </div>
        {onEdit && (
          <IconButton
            variant="quiet"
            icon={PencilSimple}
            onPress={onEdit}
            aria-label={funText.editItem(idea.title)}
          />
        )}
      </div>
      {idea.spot && <SpotInfo spot={idea.spot} compact />}
      {idea.note && <Text size="sm">{idea.note}</Text>}
      {author && (
        <Text size="sm" tone="muted">
          {funText.ideaBy(author.name)}
        </Text>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-gap-sm pt-gap-xs">
        {fans.length > 0 && (
          <span className="flex items-center">
            <span className="sr-only">
              {funText.wantGo(fans.map((m) => m.name).join(', '), fans.length)}
            </span>
            {fans.slice(0, 5).map((m) => (
              <Avatar
                key={m.id}
                name={m.name}
                color={m.color}
                emoji={m.emoji}
                size="sm"
                decorative
                className="-ml-1 first:ml-0 ring-2 ring-surface-raised"
              />
            ))}
          </span>
        )}
        {rights.me ? (
          <Button
            variant={mine ? 'primary' : 'secondary'}
            icon={Heart}
            aria-pressed={mine}
            onPress={() => void toggleHeart(f.householdId, idea.id, rights.me!, !mine)}
          >
            {mine ? funText.iWant : funText.meToo}
          </Button>
        ) : (
          <Text size="sm" tone="muted">
            {funText.pickName}
          </Text>
        )}
        {onPlan && (
          <Button variant="quiet" icon={CalendarPlus} onPress={onPlan} className="ml-auto">
            {funText.plan}
          </Button>
        )}
      </div>
    </Surface>
  );
}
